import React from 'react';
import { Scale } from 'lucide-react';

export function DisputeResolution() {
  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="relative">
          <div className="absolute inset-0 bg-royal-purple/20 rounded-full blur-lg" />
          <Scale className="w-8 h-8 text-royal-purple relative" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Dispute Resolution Policy</h1>
      </div>

      <div className="bg-white dark:bg-dark-paper rounded-xl shadow-sm">
        <div className="p-8 space-y-8">
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">1. Introduction</h2>
            <p className="text-gray-600 dark:text-gray-300">
              This Dispute Resolution Policy outlines the procedures for resolving conflicts and disagreements that may arise between users of RoyaltySuite. We are committed to providing a fair and transparent process for addressing disputes while maintaining the integrity of our community.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">2. Scope</h2>
            <p className="text-gray-600 dark:text-gray-300">
              This policy applies to all disputes related to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
              <li>Content ownership and copyright claims</li>
              <li>User behavior and interactions</li>
              <li>Account access and security</li>
              <li>Community guidelines violations</li>
              <li>Privacy concerns and data usage</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">3. Dispute Resolution Process</h2>
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">3.1 Direct Resolution</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Users are encouraged to first attempt to resolve disputes directly through our platform's messaging system. Both parties should communicate respectfully and work towards a mutual understanding.
              </p>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">3.2 Reporting System</h3>
              <p className="text-gray-600 dark:text-gray-300">
                If direct resolution is unsuccessful, users can file a formal report through our reporting system. Reports should include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
                <li>Detailed description of the issue</li>
                <li>Relevant evidence (screenshots, links, etc.)</li>
                <li>Previous attempts at resolution</li>
                <li>Desired outcome</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">3.3 Moderation Review</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Our moderation team will review all reports within 24-48 hours. They may:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
                <li>Request additional information</li>
                <li>Mediate between parties</li>
                <li>Take appropriate enforcement actions</li>
                <li>Provide recommendations for resolution</li>
              </ul>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">4. Appeals Process</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Users have the right to appeal moderation decisions within 14 days. Appeals should:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
              <li>Provide new evidence or context</li>
              <li>Explain why the decision should be reconsidered</li>
              <li>Be submitted through the designated appeals form</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">5. Enforcement Actions</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Depending on the severity and frequency of violations, enforcement actions may include:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
              <li>Warnings</li>
              <li>Content removal</li>
              <li>Temporary account restrictions</li>
              <li>Permanent account suspension</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">6. Legal Proceedings</h2>
            <p className="text-gray-600 dark:text-gray-300">
              For disputes that cannot be resolved through our internal processes:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300">
              <li>Users agree to attempt resolution through mediation before pursuing legal action</li>
              <li>Any legal proceedings must be filed in [Your Jurisdiction]</li>
              <li>The prevailing party may be entitled to recover reasonable attorney fees</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">7. Contact Information</h2>
            <p className="text-gray-600 dark:text-gray-300">
              For questions about this policy or to report issues:
            </p>
            <ul className="list-none space-y-2 text-gray-600 dark:text-gray-300">
              <li>Email: support@royaltysuite.com</li>
              <li>Support Hours: Monday - Friday, 9 AM - 5 PM EST</li>
              <li>Response Time: Within 24-48 hours</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">8. Policy Updates</h2>
            <p className="text-gray-600 dark:text-gray-300">
              We reserve the right to update this policy as needed. Users will be notified of significant changes via email and platform announcements. Continued use of the platform constitutes acceptance of any modifications to this policy.
            </p>
          </section>

          <div className="mt-8 pt-8 border-t dark:border-dark-border">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}